import { useState } from 'react';
import { useParams } from 'react-router-dom';
import type { VideoSummary } from '@streamr/shared';
import { VideoGrid } from '../components/VideoGrid';
import { SubscribeButton } from '../components/EngagementBar';
import { Avatar, ErrorState, Spinner } from '../components/ui';
import { flattenPages, useChannel, useChannelVideos } from '../api/hooks/useDiscovery';
import { useAuth } from '../auth/AuthProvider';
import { cn, formatCount } from '../lib/format';

type Sort = 'recent' | 'popular';

export const ChannelPage = (): JSX.Element => {
  // The route is /@:handle, so the param arrives without its sigil.
  const { handle = '' } = useParams<{ handle: string }>();
  const [sort, setSort] = useState<Sort>('recent');
  const { user } = useAuth();
  const channel = useChannel(handle);
  const videos = useChannelVideos(handle, sort);

  if (channel.isLoading) return <Spinner label="Loading channel" />;
  if (channel.isError) return <ErrorState error={channel.error} onRetry={() => void channel.refetch()} />;
  if (channel.data === undefined) return <ErrorState error={new Error('Channel not found')} />;

  const data = channel.data;
  const isOwner = user?.channel.id === data.id;

  return (
    <div>
      <div className="mb-4 aspect-[6/1] w-full overflow-hidden rounded-xl bg-ink-700">
        {data.bannerUrl !== null && data.bannerUrl !== '' && (
          <img src={data.bannerUrl} alt="" className="h-full w-full object-cover" />
        )}
      </div>

      <div className="mb-6 flex flex-wrap items-center gap-4">
        <Avatar src={data.avatarUrl} name={data.displayName} size={72} />
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl font-semibold">{data.displayName}</h1>
          <p className="text-sm text-ink-300">
            @{data.handle} &middot; {formatCount(data.subscriberCount)} subscribers &middot;{' '}
            {formatCount(data.videoCount)} videos
          </p>
          {data.bio !== null && data.bio !== '' && (
            <p className="mt-2 max-w-2xl whitespace-pre-wrap text-sm text-ink-200">{data.bio}</p>
          )}
        </div>
        <SubscribeButton
          channelId={data.id}
          handle={data.handle}
          isSubscribed={data.viewerIsSubscribed === true}
          isOwner={isOwner}
        />
      </div>

      <div className="mb-4 flex gap-2 border-b border-ink-700 pb-2" role="tablist" aria-label="Sort videos">
        {(['recent', 'popular'] as const).map((option) => (
          <button
            key={option}
            type="button"
            role="tab"
            aria-selected={sort === option}
            onClick={() => setSort(option)}
            className={cn(
              'rounded-full px-4 py-1.5 text-sm capitalize',
              sort === option ? 'bg-ink-700 text-ink-100' : 'text-ink-300 hover:bg-ink-800',
            )}
          >
            {option}
          </button>
        ))}
      </div>

      <VideoGrid
        videos={flattenPages<VideoSummary>(videos.data)}
        isLoading={videos.isLoading}
        error={videos.error}
        hasNextPage={videos.hasNextPage}
        isFetchingNextPage={videos.isFetchingNextPage}
        fetchNextPage={() => void videos.fetchNextPage()}
        showChannel={false}
        emptyTitle="No videos on this channel yet"
      />
    </div>
  );
};
